// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The motion DSP — pure, deterministic, and unit-tested. Everything here maps
 * raw pixels or synthetic samples to a motion score; nothing touches the DOM,
 * the camera or the network. This is the honest core: a frame is reduced to a
 * coarse grid of average brightness, and "motion" is simply how many grid cells
 * changed between two frames, within an optional zone.
 */

import type { DiffResult, MotionParams, NormRect } from './types';

/** Detection grid — coarse on purpose: robust to sensor grain, cheap to diff. */
export const GRID_COLS = 20;
export const GRID_ROWS = 15;

/** Rec.601 luma of an sRGB pixel, 0..255. */
export function luma(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Reduce an RGBA frame to a `cols × rows` grid of average luma (0..255).
 * The grid is fixed-size regardless of input resolution, so downstream cost and
 * thresholds are resolution-independent.
 */
export function computeLumaGrid(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  cols = GRID_COLS,
  rows = GRID_ROWS,
): Float32Array {
  const sum = new Float64Array(cols * rows);
  const cnt = new Float64Array(cols * rows);
  for (let y = 0; y < height; y++) {
    const gy = Math.min(rows - 1, (y * rows / height) | 0);
    const rowBase = gy * cols;
    for (let x = 0; x < width; x++) {
      const gx = Math.min(cols - 1, (x * cols / width) | 0);
      const p = (y * width + x) * 4;
      const cell = rowBase + gx;
      sum[cell] += luma(rgba[p], rgba[p + 1], rgba[p + 2]);
      cnt[cell]++;
    }
  }
  const grid = new Float32Array(cols * rows);
  for (let i = 0; i < grid.length; i++) grid[i] = cnt[i] ? sum[i] / cnt[i] : 0;
  return grid;
}

/**
 * Difference two luma grids. A cell is "active" if its brightness changed by more
 * than `cellDelta`. If `mask` is given, only cells with mask[i] !== 0 are counted
 * (both toward `total` and `activeCount`), so motion outside the zone is ignored.
 */
export function diffGrids(
  a: Float32Array,
  b: Float32Array,
  cellDelta: number,
  mask?: Uint8Array | null,
): DiffResult {
  const n = Math.min(a.length, b.length);
  const activeCells = new Uint8Array(n);
  let active = 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    if (mask && !mask[i]) continue;
    total++;
    if (Math.abs(a[i] - b[i]) > cellDelta) {
      activeCells[i] = 1;
      active++;
    }
  }
  return { activeCells, activeCount: active, total, score: total ? active / total : 0 };
}

/**
 * Map a 0..100 sensitivity to concrete thresholds. Higher sensitivity lowers both
 * the per-cell delta (smaller brightness changes count) and the fraction of cells
 * needed (smaller movements trigger).
 */
export function sensitivityToParams(sensitivity: number): MotionParams {
  const s = Math.max(0, Math.min(100, sensitivity)) / 100;
  return {
    cellDelta: lerp(38, 5, s),
    triggerFraction: lerp(0.1, 0.012, s),
  };
}

/** Build a cell mask (1 = in zone) from a normalised rectangle. */
export function maskFromRect(
  rect: NormRect | null,
  cols = GRID_COLS,
  rows = GRID_ROWS,
): Uint8Array {
  const mask = new Uint8Array(cols * rows);
  if (!rect) {
    mask.fill(1);
    return mask;
  }
  const x0 = clamp01(rect.x);
  const y0 = clamp01(rect.y);
  const x1 = clamp01(rect.x + rect.w);
  const y1 = clamp01(rect.y + rect.h);
  for (let gy = 0; gy < rows; gy++) {
    const cy = (gy + 0.5) / rows;
    for (let gx = 0; gx < cols; gx++) {
      const cx = (gx + 0.5) / cols;
      if (cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1) mask[gy * cols + gx] = 1;
    }
  }
  // Never leave a zone empty (e.g. a rect thinner than one cell) — fall back to all.
  if (!mask.some((v) => v)) mask.fill(1);
  return mask;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
